import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import { AWPLocalDB } from '@components/Common/AWPLocalDB';
import { OfflineFormStatus, FORM_MODE, NULL_OPTION, Permissions, REST_API } from '@awp/constants';
import * as Yup from 'yup';

import {
  getAvalancheGeographicAreas,
  selectGeographicAreaLinesDd,
  selectGeographicAreaPointsDd,
} from '@awp/stores/avalancheSlice';
import { selectAvalancheTechnicians } from '@awp/stores/dropdownsSlice';
import { selectCurrentUser } from '@awp/stores/usersSlice';
import { selectPhasedEventTypes } from '@awp/stores/eventSlice';

import { uuidv4 } from '@awp/helpers/Utils';
import { GetJson } from '@awp/helpers/fetchReact';
import { isValueEmpty } from '@awp/helpers/utilsReact';

import { Card, Spinner, Button, Container } from 'react-bootstrap';

import { FormTextArea, FormSelect, FormDatePicker } from '@components/Common/AwpFormInputs';
import { hasAccess } from '@components/Authorize';
import { Formik, Form } from 'formik';
import { convLocalToIsoString, getInitialTouched } from '@awp/helpers/utilsReact';
import { toast } from 'react-toastify';
import AwpYesNoDialog from '@awp/components/Common/AwpYesNoDialog';
import AwpValidationErrorModal from '@awp/components/Common/AwpValidationErrorModal';

const RcPhasedEdit = ({
  setMode,
  mode,
  setEventId,
  eventId,
  setLoading,
  loading,
  fullData,
  eventType,
  setEventTypeToActOn,
  isRoute,
  isDuplicated,
  findEvent,
}) => {
  const currentUser = useSelector(selectCurrentUser);
  const programAreas = useSelector((state) => getAvalancheGeographicAreas(state, 'AVALANCHE PROGRAM'));
  const avalancheAreas = useSelector((state) => getAvalancheGeographicAreas(state, 'AVALANCHE AREA'));
  const avalancheTechs = useSelector((state) => selectAvalancheTechnicians(state));
  const areaLines = useSelector((state) => selectGeographicAreaLinesDd(state));
  const areaPoints = useSelector((state) => selectGeographicAreaPointsDd(state));
  const phasedEventTypesRaw = useSelector(selectPhasedEventTypes);
  const [phasedEventTypes, setPhasedEventTypes] = useState(phasedEventTypesRaw);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const nullOption = NULL_OPTION;
  const defaultDto = {
    program: null,
    area: null,
    segment: null,
    fromPoint: null,
    toPoint: null,
    rc_timestamp: null,
    event_type: phasedEventTypes.find((x) => x.value === eventType),
    timestamp: new Date(),
    comment: '',
  };

  const [dto, setDto] = useState(defaultDto);
  const [eventErrors, setEventErrors] = useState(null);
  const [formStatus, setFormStatus] = useState(null);
  const [isAvalancheTech, setIsAvalancheTech] = useState(true);

  const avalanchePrograms = mode === FORM_MODE.CREATE ? programAreas.filter((x) => !x.isDisabled) : programAreas;
  const [areaList, setAreaList] = useState(
    mode === FORM_MODE.CREATE ? avalancheAreas.filter((x) => !x.isDisabled) : avalancheAreas,
  );
  const [lineList, setLineList] = useState(
    mode === FORM_MODE.CREATE ? areaLines.filter((x) => !x.isDisabled) : areaLines,
  );
  const [pointList, setPointList] = useState(
    mode === FORM_MODE.CREATE ? areaPoints.filter((x) => !x.isDisabled) : areaPoints,
  );

  const readOnly = mode !== FORM_MODE.EDIT && mode !== FORM_MODE.CREATE;
  const buttonLabel = 'Save';

  const title =
    mode === FORM_MODE.EDIT ? 'Edit Phased Event' : mode === FORM_MODE.CREATE ? 'Create Phased Event' : 'Phased Event';

  const getProgramByAreaValue = (areaValue) => {
    const area = areaList.find((x) => x.value === areaValue);
    return avalanchePrograms.find((x) => x.value === area?.parentId);
  };

  useEffect(() => {
    if (mode === FORM_MODE.CREATE || mode === FORM_MODE.EDIT) {
      setIsAvalancheTech(avalancheTechs.options.findIndex((t) => t.value === currentUser.SecurityIdentifierId) !== -1);
    }
    if (mode === FORM_MODE.CREATE) {
      setFormStatus(OfflineFormStatus.Invalid);
      return;
    }

    setLoading(true);
    if (isRoute) {
      fetchRouted();
    } else {
      AWPLocalDB.avalancheData
        .get(eventId)
        .then((entity) => {
          if (!entity) {
            entity = findEvent(fullData, eventId);
          }
          setUnprocessedEvent(entity);
          setDisabledPhasedEvents(entity);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, []);

  const setDisabledPhasedEvents = (entity) => {
    const phasedEvents = fullData.filter(
      (x) =>
        x.userId === currentUser.SecurityIdentifierId &&
        phasedEventTypesRaw.find((y) => y.value === x.event_type) &&
        x.rc_timestamp === entity.rc_timestamp &&
        ((entity.road_closure_segment && x.road_closure_segment === entity.road_closure_segment) ||
          (x.road_closure_points?.length === 2 &&
            entity.road_closure_points?.length === 2 &&
            x.road_closure_points[0] === entity.road_closure_points[0] &&
            x.road_closure_points[1] === entity.road_closure_points[1])),
    );

    let excluded = [];
    const filteredData = fullData.filter((x) => x.id == entity.parent_trace_id);
    for (let index = 0; index < filteredData.length; index++) {
      const element = filteredData[index];

      const matchingExcluded = phasedEventTypesRaw.filter((x) =>
        element.ChildIngestionCandidates.some((y) => y.event_type === x.value),
      );

      excluded = excluded.concat(matchingExcluded);
    }

    const list = phasedEventTypesRaw.map((x) => {
      if (x.value === entity.event_type) return x;

      if (phasedEvents.find((p) => p.event_type === x.value) || excluded.find((p) => p.value === x.value)) {
        return { ...x, isDisabled: true };
      }
      return x;
    });

    setPhasedEventTypes(list);
  };

  const setUnprocessedEvent = (entity) => {
    const dtoTemp = {
      program: getProgramByAreaValue(entity.avalanche_area),
      area: avalancheAreas.find((x) => x.value === entity.avalanche_area),
      segment: entity.road_closure_segment ? areaLines.find((x) => x.value === entity.road_closure_segment) : null,
      fromPoint: entity.road_closure_points ? areaPoints.find((x) => x.value === entity.road_closure_points[0]) : null,
      toPoint: entity.road_closure_points ? areaPoints.find((x) => x.value === entity.road_closure_points[1]) : null,
      rc_timestamp: entity.rc_timestamp ? new Date(entity.rc_timestamp) : null,
      event_type: phasedEventTypes.find((x) => x.value === entity.event_type),
      timestamp: new Date(entity.timestamp),
      comment: entity.comment,
    };

    setDto(dtoTemp);
    setFormStatus(entity.status);
    dtoTemp.program && setAreaList(avalancheAreas.filter((x) => x.parentId === dtoTemp.program.value));
    dtoTemp.area && setLineList(areaLines.filter((x) => x.areaId === dtoTemp.area.value));
    dtoTemp.area && setPointList(areaPoints.filter((x) => x.areaId === dtoTemp.area.value));
  };
  const fetchRouted = () => {
    const url = REST_API.INGESTION_CANDIDATE_BY_TRACEID + eventId;
    GetJson(url)
      .then((response) => response.json())
      .then((occurrence) => {
        if (occurrence && !isValueEmpty(occurrence.JsonObject)) setUnprocessedEvent(JSON.parse(occurrence.JsonObject));
      })
      .finally(() => {
        setLoading(false);
      });
  };
  const programOnChange = (opt, setFieldValue) => {
    setFieldValue('area', nullOption, false);
    setAreaList(avalancheAreas.filter((x) => x.parentId === opt.value));

    setFieldValue('segment', nullOption, false);
    setLineList(areaLines.filter((x) => x.programId === opt.value));

    setFieldValue('fromPoint', nullOption, false);
    setFieldValue('toPoint', nullOption, false);
    setPointList(areaPoints.filter((x) => x.programId === opt.value));
  };

  const areaOnChange = (opt, setFieldValue) => {
    setFieldValue('segment', nullOption, false);
    setLineList(areaLines.filter((x) => x.areaId === opt.value));

    setFieldValue('fromPoint', nullOption, false);
    setFieldValue('toPoint', nullOption, false);
    setPointList(areaPoints.filter((x) => x.areaId === opt.value));

    setFieldValue(
      'program',
      avalanchePrograms.find((x) => x.value === opt.parentId),
    );
  };

  const segmentOnChange = (opt, setFieldValue) => {
    const area = avalancheAreas.find((x) => x.value === opt.areaId);
    setAreaList(avalancheAreas.filter((x) => x.parentId === area.parentId));
    setFieldValue('area', area, false);

    if (opt) {
      setFieldValue('fromPoint', nullOption, false);
      setFieldValue('toPoint', nullOption, false);
    }

    setFieldValue('program', getProgramByAreaValue(area.value), false);

    setLineList(areaLines.filter((x) => x.areaId === opt.areaId));
    setPointList(pointList.filter((x) => x.areaId === opt.areaId));

    setFieldValue('fromPoint', nullOption, false);
    setFieldValue('toPoint', nullOption, false);
  };

  const pointOnChange = (opt, setFieldValue) => {
    const area = avalancheAreas.find((x) => x.value === opt.areaId);
    setAreaList(avalancheAreas.filter((x) => x.parentId === area.parentId));
    setFieldValue('area', area, false);

    setFieldValue('program', getProgramByAreaValue(area.value), false);

    setLineList(areaLines.filter((x) => x.areaId === opt.areaId));
    setPointList(pointList.filter((x) => x.areaId === opt.areaId));

    if (opt) setFieldValue('segment', nullOption, false);
  };

  const dtoToEntity = (id, dto) => {
    const traceId = id ?? uuidv4();
    return {
      id: traceId,
      provider: 'INTERNAL_INGESTION_' + currentUser.Env.toUpperCase(),
      trace_id: traceId,
      rc_timestamp: dto.rc_timestamp ? convLocalToIsoString(dto.rc_timestamp) : null,
      timestamp: convLocalToIsoString(dto.timestamp ?? new Date()),
      event_type: dto.event_type?.value,
      avalanche_area: dto.area?.value,
      road_closure_segment: dto.segment?.value,
      road_closure_points: dto.segment && dto.segment.value > 0 ? null : [dto.fromPoint?.value, dto.toPoint?.value],
      comment: dto.comment,
      userName: currentUser.UserName,
      userType: currentUser.UserType,
      userId: currentUser.SecurityIdentifierId,
      status: dto.status,
      internal: {
        avalanche_program: dto.program,
        avalanche_area: dto.area,
        segment: dto.segment,
        fromPoint: dto.fromPoint,
        toPoint: dto.toPoint,
        event_type: dto.event_type,
      },
      lastUpdated: Date.now(),
    };
  };

  const updateEntity = (setSubmitting, setErrors, dto) => {
    const entity = dtoToEntity(eventId, dto);
    AWPLocalDB.avalancheData
      .update(eventId, entity)
      .then(async (updated) => {
        if (updated) {
          toast.success('Road closure has been updated');
        } else {
          const errors = { id: `Update failed. There was no road closure data with Id: ${eventId}` };
          setErrors(errors);
          if (errors.occurrence) setEventErrors({ Occurrence: errors.occurrence });
        }
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  const createEntity = (setSubmitting, setErrors, dto) => {
    const entity = dtoToEntity(null, dto);

    AWPLocalDB.avalancheData
      .add(entity)
      .then(async () => {
        toast.success('Phased event has been created');
        setEventTypeToActOn(entity.internal.event_type);
        setEventId(entity.id);
        setMode('edit');
      })
      .catch((error) => {
        const errors = { InsertError: error };
        setErrors(errors);
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  const handleSubmit = ({ values, setSubmitting, validateForm, setTouched, setErrors }) => {
    let status = OfflineFormStatus.Valid;

    validateForm(values).then((errors) => {
      if (Object.keys(errors).length > 0) {
        setTouched(errors);
        status = OfflineFormStatus.Invalid;
      }
      setFormStatus(status);
      saveForm({ ...values, status }, setSubmitting, setErrors);
    });
  };

  const saveForm = (values, setSubmitting, setErrors) => {
    setSubmitting(true);

    if (mode === FORM_MODE.EDIT) {
      updateEntity(setSubmitting, setErrors, values);
    } else {
      createEntity(setSubmitting, setErrors, values);
    }
  };

  const handleBtnClose = () => {
    setMode(FORM_MODE.LIST);
  };

  const breadCrumb = () => {
    const entity = findEvent(fullData, eventId);
    const name = entity?.internal.event_type?.label;
    return name ? name : eventId;
  };

  const Buttons = ({ props }) => {
    return mode !== FORM_MODE.VIEW ? (
      <>
        <div className="mr-3" style={{ float: 'left' }}>
          {!showCancelConfirm && (
            <Button
              className="mr-2"
              style={{ marginTop: '40px' }}
              variant="outline-primary"
              onClick={() => {
                setShowCancelConfirm(true);
              }}
            >
              {'Close'}
            </Button>
          )}

          {!showCancelConfirm && isAvalancheTech && hasAccess([Permissions.WriteAvalancheForm]) && (
            <Button
              type="button"
              style={{ marginTop: '40px' }}
              variant="primary"
              onClick={() => handleSubmit(props)}
              disabled={props.isSubmitting}
            >
              {props.isSubmitting ? (
                <>
                  <Spinner as="span" animation="grow" size="sm" role="status" aria-hidden="true" />
                  Saving...
                </>
              ) : (
                <span>{buttonLabel}</span>
              )}
            </Button>
          )}
          {showCancelConfirm && (
            <AwpYesNoDialog
              action={() => handleBtnClose(props)}
              setShowYesNoConfirm={setShowCancelConfirm}
              msg={'Are you sure you want to close?'}
              props={{}}
            />
          )}
        </div>
      </>
    ) : (
      <Button
        className="mr-2"
        variant="outline-primary"
        style={{ marginTop: '40px' }}
        onClick={() => {
          handleBtnClose(props);
        }}
      >
        {'Close'}
      </Button>
    );
  };

  Buttons.propTypes = {
    title: PropTypes.string,
    props: PropTypes.any,
    resetForm: PropTypes.func,
    isSubmitting: PropTypes.bool,
    isValid: PropTypes.bool,
    dirty: PropTypes.bool,
  };

  return (
    <>
      {loading ? (
        <Container>
          <Spinner animation="border" />
        </Container>
      ) : (
        <>
          <div style={{ paddingBottom: '10px' }}>
            <span className="awp-text-btn" onClick={() => setMode(FORM_MODE.LIST)}>
              Avalanche Events List
            </span>
            <span className="mx-3">&gt;</span>
            <span>{breadCrumb()}</span>
          </div>
          <Formik
            enableReinitialize
            validateOnMount
            validateOnBlur
            validateOnChange
            initialValues={dto}
            initialTouched={getInitialTouched(dto, mode === 'edit')}
            validationSchema={Yup.object({
              program: Yup.object()
                .nullable()
                .test('program', 'Program is a required field', (value, context) => {
                  if (context.parent.program && context.parent.program.value) return true;
                  return false;
                }),
              area: Yup.object()
                .nullable()
                .test('area', 'Area is a required field', (value, context) => {
                  if (context.parent.area && context.parent.area.value) return true;
                  return false;
                }),
              segment: Yup.object()
                .nullable()
                .test('location', 'Either Road Closure Area or From/To is required', (value, context) => {
                  if (
                    (context.parent.segment && context.parent.segment.value) ||
                    (context.parent.fromPoint && context.parent.fromPoint.value) ||
                    (context.parent.toPoint && context.parent.toPoint.value)
                  )
                    return true;
                  return false;
                }),
              fromPoint: Yup.object()
                .nullable()
                .test('location', 'Either Road Closure Area or From/To is required', (value, context) => {
                  if (
                    (context.parent.segment && context.parent.segment.value) ||
                    ((!context.parent.segment || !context.parent.segment.value) &&
                      context.parent.fromPoint &&
                      context.parent.fromPoint.value)
                  )
                    return true;
                  return false;
                }),
              toPoint: Yup.object()
                .nullable()
                .test('location', 'Either Road Closure Area or From/To is required', (value, context) => {
                  if (
                    (context.parent.segment && context.parent.segment.value) ||
                    ((!context.parent.segment || !context.parent.segment.value) &&
                      context.parent.toPoint &&
                      context.parent.toPoint.value)
                  )
                    return true;
                  return false;
                }),
              comment: Yup.string().optional().label('Comment').max(500, '${label} must not exceed 500 characters'),
              rc_timestamp: Yup.string().nullable().optional(),
              event_type: Yup.object().nullable().required('Phased Event type is required'),
              timestamp: Yup.date().nullable().required('Phased Event Date is required'),
            })}
            onSubmit={handleSubmit}
          >
            {(props) => (
              <>
                <div className="d-flex">
                  <h1 className="flex-grow-1">{title}</h1>
                </div>
                <Form>
                  <Card
                    border="light"
                    className="page-card"
                    style={{ border: '0px', boxShadow: '0px 0px 0px #000000', marginTop: '0px' }}
                  >
                    <Card.Body className="pr-pl-pt-0">
                      <FormSelect
                        className="avalanche-select"
                        useMargin={true}
                        readOnly={readOnly}
                        name="program"
                        id="program"
                        label="Program"
                        postChange={(e) => {
                          programOnChange(e, props.setFieldValue);
                        }}
                        options={avalanchePrograms}
                        isMulti={false}
                      />

                      <FormSelect
                        className="avalanche-select"
                        useMargin={true}
                        readOnly={readOnly}
                        name="area"
                        id="area"
                        label="Avalanche Area"
                        postChange={(e) => {
                          areaOnChange(e, props.setFieldValue);
                        }}
                        options={areaList}
                        isMulti={false}
                      />

                      <FormSelect
                        className="avalanche-select"
                        useMargin={true}
                        readOnly={readOnly}
                        name="segment"
                        id="segment"
                        label="Road Closure Area"
                        postChange={(e) => {
                          segmentOnChange(e, props.setFieldValue);
                        }}
                        options={lineList}
                        isMulti={false}
                      />

                      <FormSelect
                        className="avalanche-select"
                        useMargin={true}
                        readOnly={readOnly}
                        name="fromPoint"
                        id="fromPoint"
                        label="From"
                        postChange={(e) => {
                          pointOnChange(e, props.setFieldValue);
                        }}
                        options={pointList}
                        isMulti={false}
                      />

                      <FormSelect
                        className="avalanche-select"
                        useMargin={true}
                        readOnly={readOnly}
                        name="toPoint"
                        id="toPoint"
                        label="To"
                        postChange={(e) => {
                          pointOnChange(e, props.setFieldValue);
                        }}
                        options={pointList}
                        isMulti={false}
                      />
                      <div className="customWidthDatePicker mb-20">
                        <FormDatePicker
                          readOnly={readOnly}
                          label="Road Closed"
                          id="rc_timestamp"
                          name="rc_timestamp"
                          showTimeSelect={true}
                          showTime={{ isEnabled: true }}
                          timeFormat="HH:mm"
                          timeIntervals={30}
                          dateFormat={['yyyy-MM-dd HH:mm']}
                        />
                      </div>
                      <FormSelect
                        className="avalanche-select"
                        useMargin={true}
                        readOnly={readOnly}
                        name="event_type"
                        id="event_type"
                        label="Phased Event"
                        options={phasedEventTypes}
                        isMulti={false}
                        focus={!isDuplicated && mode === FORM_MODE.EDIT}
                      />
                      <div className="customWidthDatePicker mb-20">
                        <FormDatePicker
                          readOnly={readOnly}
                          label="Date/Time"
                          id="timestamp"
                          name="timestamp"
                          showTimeSelect={true}
                          showTime={{ isEnabled: true }}
                          timeFormat="HH:mm"
                          timeIntervals={30}
                          dateFormat={['yyyy-MM-dd HH:mm']}
                          focus={isDuplicated && mode === FORM_MODE.EDIT}
                        />
                      </div>

                      <FormTextArea readOnly={readOnly} label="Comment" id="comment" name="comment" rows={3} />
                      {mode !== FORM_MODE.CREATE ? (
                        <div>
                          <b>Form Status:</b>{' '}
                          <span
                            style={formStatus === OfflineFormStatus.Invalid ? { color: 'red' } : { color: 'black' }}
                          >
                            {formStatus}
                          </span>
                        </div>
                      ) : null}
                      <Buttons title=" " props={props} />
                    </Card.Body>
                  </Card>
                </Form>
              </>
            )}
          </Formik>
          <AwpValidationErrorModal setErrors={setEventErrors} errors={eventErrors} />
        </>
      )}
    </>
  );
};

RcPhasedEdit.propTypes = {
  setMode: PropTypes.func,
  mode: PropTypes.string,
  setEventId: PropTypes.func,
  eventId: PropTypes.string,
  setLoading: PropTypes.func,
  loading: PropTypes.bool,
  setFieldValue: PropTypes.func,
  validateField: PropTypes.func,
  setFieldTouched: PropTypes.func,
  values: PropTypes.any,
  resetForm: PropTypes.func,
  fullData: PropTypes.array,
  eventType: PropTypes.string,
  setEventTypeToActOn: PropTypes.func,
  isDuplicated: PropTypes.bool,
};

export default RcPhasedEdit;
